import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  CustomerMigrationImportService,
  MigrationImportHistoryItem,
} from '../../services/customer-migration-import.service';
import {
  VendorAdapterInfo,
  VendorCredentialStatus,
  VendorSyncService,
} from '../../services/vendor-sync.service';
import {
  VendorPlanMapping,
  VendorPlanMappingService,
} from '../../services/vendor-plan-mapping.service';
import { AddButtonComponent } from '../../../shared/components/add-button/add-button.component';
import { ImportDataComponent } from '../import-data/import-data.component';
import { VendorSystemComponent } from './vendor-system.component';
import { environment } from '../../../../environments/environment';
import {
  HISTORY_PAGE_SIZE,
  VendorSystem,
  addableVendors,
  deriveVendorSystem,
  listedVendors,
  piiMaskingBadge,
} from './vendor-migration.model';

/**
 * The Customer Migration tab.
 *
 * Replaces a source dropdown that mixed two unlike things: one-off file imports
 * (an action) and vendor syncs (a persistent connection with credentials, a
 * schedule and a plan mapping). A vendor's state used to be invisible until you
 * selected it, and there was no view that answered "which systems is this wash
 * on?" at all.
 *
 * Here each system this car wash actually uses is a row whose state is readable
 * without selecting anything, and the vendor bridge is presented as what it is —
 * temporary, with an explicit end. Adapters the wash has never used are not rows;
 * they live behind the Add picker, so the list stays about this car wash rather
 * than becoming a catalogue of everything Way can talk to.
 */
@Component({
  selector: 'app-customer-migration',
  standalone: true,
  // This app keeps Zone + Eager (see eslint.config.js): Angular 22 defaults new
  // components to OnPush, and under OnPush these panels load their data but never
  // repaint, leaving the tab stuck on "Loading..." forever.
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, ImportDataComponent, VendorSystemComponent, AddButtonComponent],
  templateUrl: './customer-migration.component.html',
  styleUrls: ['./customer-migration.component.scss'],
})
export class CustomerMigrationComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) companyId!: string;
  /** Used to gate the cut-over confirmation on the car wash's own name. */
  @Input() companyName = '';

  /**
   * Which pane is showing. Deliberately component state rather than routes: the
   * tab's URL is owned by company-detail, and adding child routes would make this
   * feature touch admin routing that other tabs depend on.
   */
  view: 'list' | 'file' | 'system' = 'list';
  /** CWS-1837: what this environment does with customer email and phone on save. */
  readonly piiMasking = piiMaskingBadge(environment);
  /** Vendor whose detail pane is open, when `view === 'system'`. */
  openVendor: string | null = null;
  showPicker = false;

  adapters: VendorAdapterInfo[] = [];
  statuses: Record<string, VendorCredentialStatus | null> = {};
  mappings: Record<string, VendorPlanMapping | null> = {};
  history: MigrationImportHistoryItem[] = [];
  historyTruncated = false;

  loading = false;
  loadError = '';
  /**
   * Set when part of the load failed but the rest succeeded.
   *
   * The per-request catches below deliberately keep the pane usable when one
   * vendor's status is unavailable — but on their own they turned an outage into
   * a confident lie: with svc-vendor-sync down, adapters came back empty, so the
   * pane rendered "Nothing connected yet" for a car wash that has bridges. This
   * flag is what stops silence being reported as absence.
   */
  loadDegraded = false;

  private readonly importService = inject(CustomerMigrationImportService);
  private readonly vendorSyncService = inject(VendorSyncService);
  private readonly planMappingService = inject(VendorPlanMappingService);
  private loadSub: Subscription | null = null;

  ngOnInit(): void {
    if (this.companyId) this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['companyId'] || changes['companyId'].firstChange) return;
    // Everything here is per company, so a switch must discard the previous
    // answers rather than render them against the new car wash.
    this.view = 'list';
    this.openVendor = null;
    this.showPicker = false;
    this.statuses = {};
    this.mappings = {};
    this.history = [];
    if (this.companyId) {
      this.reload();
    } else {
      this.stopLoad();
      this.adapters = [];
    }
  }

  ngOnDestroy(): void {
    this.stopLoad();
  }

  /** Systems this car wash uses, worst news first so problems are not below the fold. */
  get systems(): VendorSystem[] {
    const listed = listedVendors(this.adapters, this.statuses, this.history);
    const derived = listed.map(adapter =>
      deriveVendorSystem({
        adapter,
        credentialStatus: this.statuses[adapter.name] ?? null,
        planMapping: this.mappings[adapter.name] ?? null,
        history: this.history,
        historyTruncated: this.historyTruncated,
      })
    );
    const rank: Record<string, number> = {
      broken: 0,
      setup_incomplete: 1,
      needs_attention: 2,
      backfilling: 3,
      ready_to_cut_over: 4,
      bridging: 5,
      migrated: 6,
    };
    return derived.sort((a, b) => (rank[a.state] ?? 9) - (rank[b.state] ?? 9));
  }

  get addableAdapters(): VendorAdapterInfo[] {
    return addableVendors(this.adapters, listedVendors(this.adapters, this.statuses, this.history));
  }

  get openSystem(): VendorSystem | null {
    if (!this.openVendor) return null;
    return this.systems.find(s => s.vendor === this.openVendor) ?? null;
  }

  /**
   * Load everything the list derives from.
   *
   * Adapters first, because the credential calls are per adapter. Individual
   * failures degrade to null rather than failing the page: a car wash whose
   * SiteWatch status is unreachable should still see its EverWash row and the
   * file-import path.
   */
  reload(): void {
    this.stopLoad();
    const companyId = this.companyId;
    if (!companyId) return;
    this.loading = true;
    this.loadError = '';
    this.loadDegraded = false;
    // Collected in the closure rather than written straight to the field: the
    // catches run before the stale-company guard, and a discarded response must
    // not leave a warning on the pane for the company the operator switched to.
    let degraded = false;

    this.loadSub = this.vendorSyncService
      .listVendors()
      .pipe(
        map(response => response.data?.adapters ?? []),
        catchError(() => {
          degraded = true;
          return of([] as VendorAdapterInfo[]);
        }),
        switchMap(adapters => {
          this.adapters = adapters;
          const statuses = adapters.length
            ? forkJoin(
                adapters.map(adapter =>
                  this.vendorSyncService.getCredentialStatus(adapter.name, companyId).pipe(
                    map(r => ({ vendor: adapter.name, status: r.data ?? null })),
                    catchError(() => {
                      degraded = true;
                      return of({ vendor: adapter.name, status: null });
                    })
                  )
                )
              )
            : of([] as { vendor: string; status: VendorCredentialStatus | null }[]);
          const history = this.importService.getHistory(companyId, HISTORY_PAGE_SIZE).pipe(
            map(r => ({
              rows: r.data ?? [],
              // The server now reports the true total, so whether older runs exist
              // is a fact rather than an inference from a full-looking page.
              total: r.pagination?.total_records ?? null,
            })),
            catchError(() => {
              degraded = true;
              return of({ rows: [] as MigrationImportHistoryItem[], total: null });
            })
          );
          return forkJoin({ statuses, history });
        }),
        switchMap(({ statuses, history }) => {
          if (companyId !== this.companyId) return of(null);
          this.loadDegraded = degraded;
          this.statuses = {};
          statuses.forEach(entry => (this.statuses[entry.vendor] = entry.status));
          this.history = history.rows;
          // Older runs exist beyond this page, so the earliest row we hold is not
          // necessarily the first sync and the model states a floor rather than a
          // date. Falls back to the old inference only if pagination is absent,
          // which would mean an older backend.
          this.historyTruncated =
            history.total === null
              ? history.rows.length >= HISTORY_PAGE_SIZE
              : history.total > history.rows.length;

          const listed = listedVendors(this.adapters, this.statuses, this.history);
          if (!listed.length)
            return of([] as { vendor: string; mapping: VendorPlanMapping | null }[]);
          return forkJoin(
            listed.map(adapter =>
              this.planMappingService.getPlanMapping(companyId, adapter.name).pipe(
                map(r => ({ vendor: adapter.name, mapping: r.data ?? null })),
                catchError(() => {
                  degraded = true;
                  return of({ vendor: adapter.name, mapping: null });
                })
              )
            )
          );
        })
      )
      .subscribe({
        next: mappings => {
          if (companyId !== this.companyId) return;
          this.loadDegraded = degraded;
          this.loading = false;
          this.loadSub = null;
          if (mappings) {
            this.mappings = {};
            mappings.forEach(entry => (this.mappings[entry.vendor] = entry.mapping));
          }
        },
        error: () => {
          if (companyId !== this.companyId) return;
          this.loading = false;
          this.loadSub = null;
          this.loadError =
            'Could not load this car wash’s migration state. The file import below still works.';
        },
      });
  }

  openSystemDetail(vendor: string): void {
    this.openVendor = vendor;
    this.view = 'system';
    this.showPicker = false;
  }

  /** Picking from the Add picker goes straight to Connect — adding *is* connecting. */
  startAdding(adapter: VendorAdapterInfo): void {
    this.showPicker = false;
    this.openSystemDetail(adapter.name);
  }

  backToList(): void {
    this.view = 'list';
    this.openVendor = null;
    // The detail pane may have saved credentials, a mapping, or cut a system
    // over, so the list must be re-derived from the server rather than reused.
    this.reload();
  }

  openFileImport(): void {
    this.view = 'file';
    this.showPicker = false;
  }

  togglePicker(): void {
    this.showPicker = !this.showPicker;
  }

  /** Adapter behind the open detail pane, including one being added for the first time. */
  get openAdapter(): VendorAdapterInfo | null {
    return this.adapters.find(a => a.name === this.openVendor) ?? null;
  }

  trackByVendor(_index: number, system: VendorSystem): string {
    return system.vendor;
  }

  trackByName(_index: number, adapter: VendorAdapterInfo): string {
    return adapter.name;
  }

  private stopLoad(): void {
    this.loadSub?.unsubscribe();
    this.loadSub = null;
    this.loading = false;
  }
}
